// src/components/EmployeeEmploymentSection.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Collapse,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { resolveTenantId } from "../utils/tenant";
import { normalizePunchId, updateEmployeeIdentityTransaction } from "../utils/employeeUniqueness";
export default function EmployeeEmploymentSection({ employeeId }) {
  const [fields, setFields] = useState({
    startDate: "",
    employmentType: "",
    currentStatus: "",
    eligibleForOT: false,
    endDate: "",
    terminationReason: "",
    password: "", // Employee login password
    punchId: "", // 4-digit punch ID for time clock
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [originalFields, setOriginalFields] = useState(null);

  const hasChanges = !!originalFields && JSON.stringify(fields) !== JSON.stringify(originalFields);

  // Generate a unique 4-digit punch ID
  const generateUniquePunchId = async () => {
    let attempts = 0;
    const maxAttempts = 100;

    // Resolve tenantId once
    let tenantId = null;
    const adminRaw = localStorage.getItem('adminUser');
    if (adminRaw) {
      try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
    }
    if (!tenantId && auth && auth.currentUser) {
      const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
      const utSnap = await getDoc(utRef);
      if (utSnap.exists()) {
        const ut = utSnap.data();
        tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
      }
    }
    if (!tenantId) throw new Error('Tenant not found');

    while (attempts < maxAttempts) {
      const punchId = Math.floor(1000 + Math.random() * 9000).toString();
      try {
        // Uniqueness check via lock doc
        const snap = await getDoc(doc(db, `tenants/${tenantId}/punchIds`, punchId));
        if (!snap.exists() || snap.data()?.employeeId === employeeId) return punchId;
      } catch (error) {
        console.error("Error checking punch ID lock:", error);
        break;
      }
      attempts++;
    }

    // fallback
    return Date.now().toString().slice(-4);
  };

  const handleGeneratePunchId = async () => {
    const newPunchId = await generateUniquePunchId();
    setFields(f => ({ ...f, punchId: newPunchId }));
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      if (!employeeId) {
        setLoading(false);
        return;
      }
      // Resolve tenant and read tenant-scoped employee doc
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
        const utSnap = await getDoc(utRef);
        if (utSnap.exists()) {
          const ut = utSnap.data();
          tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
        }
      }
      if (!tenantId) {
        setLoading(false);
        setErrorMsg('Tenant not found');
        return;
      }
      const ref = doc(db, `tenants/${tenantId}/employees`, employeeId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const d = snap.data();
        const employmentData = d.employment || {};
        const newFields = {
          startDate: employmentData.startDate || "",
          employmentType: employmentData.employmentType || "",
          currentStatus: employmentData.currentStatus || "Active",
          eligibleForOT: employmentData.eligibleForOT || false,
          endDate: employmentData.endDate || "",
          terminationReason: employmentData.terminationReason || "",
          password: employmentData.password || "Welcome1", // Default password
          punchId: employmentData.punchId || "", // 4-digit punch ID
        };
        setFields(newFields);
        setOriginalFields(newFields);
      }
      setLoading(false);
    }
    load();
  }, [employeeId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFields((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    if (!fields.startDate.trim()) return "Start Date is required.";
    if (!fields.employmentType.trim()) return "Employment Type is required.";
    if (!fields.currentStatus.trim()) return "Current Status is required.";
    
    // Punch ID validation
    if (fields.punchId && fields.punchId.length !== 4) {
      return "Punch ID must be exactly 4 digits.";
    }
    
  // If terminated, end date is required
  if (fields.currentStatus && fields.currentStatus.toString().toLowerCase() === "terminated" && !fields.endDate.trim()) {
      return "End Date is required for terminated employees.";
    }
    
    // End date should be after start date
    if (fields.endDate && fields.startDate) {
      const startDate = new Date(fields.startDate);
      const endDate = new Date(fields.endDate);
      if (endDate <= startDate) {
        return "End Date must be after Start Date.";
      }
    }
    
    return "";
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      setErrorMsg(error);
      setSuccessMsg("");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    try {
      const tenantId = await resolveTenantId();

      await updateEmployeeIdentityTransaction({
        tenantId,
        employeeId,
        updates: {
          employment: {
            ...fields,
            punchId: normalizePunchId(fields.punchId),
          },
        },
      });
      setSuccessMsg("Employment information saved!");
      setEdit(false);
      setOriginalFields(fields);
    } catch (err) {
      if (err && err.message === 'employee-punchid-taken') {
        setErrorMsg('Failed to save: Punch ID is already in use in this tenant.');
      } else {
        setErrorMsg("Failed to save: " + (err.message || "Unknown error."));
      }
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setFields(originalFields || fields);
    setEdit(false);
    setErrorMsg("");
    setSuccessMsg("");
  };

  if (loading) return <Typography>Loading employment information...</Typography>;

  const isTerminated = fields.currentStatus && fields.currentStatus.toString().toLowerCase() === "terminated";
  const employmentSummaryItems = [
    { label: 'Start Date', value: fields.startDate || 'Not provided' },
    { label: 'Employment Type', value: fields.employmentType || 'Not provided' },
    { label: 'Current Status', value: fields.currentStatus || 'Not provided' },
    { label: 'Punch ID', value: fields.punchId || 'Not assigned' },
    { label: 'Employee Login Password', value: fields.password || 'Not set', wide: true },
  ];
  if (fields.endDate) employmentSummaryItems.push({ label: 'End Date', value: fields.endDate });
  if (fields.terminationReason) employmentSummaryItems.push({ label: 'Termination Reason', value: fields.terminationReason, wide: true });

  const lengthOfService = fields.startDate
    ? (() => {
        const startDate = new Date(fields.startDate);
        const endDate = fields.endDate ? new Date(fields.endDate) : new Date();
        const diffTime = Math.abs(endDate - startDate);
        const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));
        const diffMonths = Math.floor((diffTime % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));

        if (diffYears > 0) {
          return `${diffYears} year${diffYears > 1 ? 's' : ''}, ${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
        }
        return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
      })()
    : 'Not available';

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 1120,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(156, 39, 176, 0.04)',
        overflow: 'hidden',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
      }}
    >
      <Box sx={{ px: 2.25, py: 1.5, bgcolor: 'rgba(255,255,255,0.68)', backdropFilter: 'blur(4px)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.08rem' }}>
              Employment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.88rem' }}>
              Work status, access details, overtime eligibility, and time-clock setup.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {!edit ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setEdit(true);
                  setSuccessMsg("");
                  setErrorMsg("");
                  setOriginalFields(fields);
                }}
                sx={{
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 1.25,
                  minHeight: 36,
                }}
              >
                ✏️ Edit
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 2.25, pb: 2.25, pt: 1 }}>
      <Collapse in={!!successMsg || !!errorMsg} sx={{ mt: 1 }}>
        {successMsg && (
          <Alert
            severity="success"
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setSuccessMsg("")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ mb: 1 }}
          >
            {successMsg}
          </Alert>
        )}
        {errorMsg && (
          <Alert
            severity="error"
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setErrorMsg("")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ mb: 1 }}
          >
            {errorMsg}
          </Alert>
        )}
      </Collapse>

  {!edit ? (
      <Stack spacing={2.25} mt={1.5}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.1,
          }}
        >
          <Box
            sx={{
              p: 1.2,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha('#ffffff', 0.72),
            }}
          >
            <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.6, fontSize: '0.68rem', lineHeight: 1.1 }}>
              Current Status
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 900, fontSize: '1.02rem' }}>
              {fields.currentStatus || 'Not provided'}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1.2,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha('#ffffff', 0.72),
            }}
          >
            <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.6, fontSize: '0.68rem', lineHeight: 1.1 }}>
              Length of Service
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 900, fontSize: '1.02rem' }}>
              {lengthOfService}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1.2,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha('#ffffff', 0.72),
            }}
          >
            <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.6, fontSize: '0.68rem', lineHeight: 1.1 }}>
              Overtime
            </Typography>
            <Box sx={{ mt: 0.55 }}>
              <Chip
                label={fields.eligibleForOT ? 'Eligible' : 'Not eligible'}
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: fields.eligibleForOT ? alpha('#2563eb', 0.08) : alpha('#6b7280', 0.08),
                  color: fields.eligibleForOT ? 'primary.main' : 'text.secondary',
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.1,
          }}
        >
          {employmentSummaryItems.map((item) => (
            <Box
              key={item.label}
              sx={{
                p: 1.2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha('#ffffff', 0.72),
                gridColumn: item.wide ? { md: 'span 3' } : undefined,
              }}
            >
              <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.6, fontSize: '0.68rem', lineHeight: 1.1 }}>
                {item.label}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.32, fontWeight: 700, lineHeight: 1.38, fontSize: '1rem' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
      ) : (
      <Stack spacing={3} mt={2}>
        {/* Basic Employment Information */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Basic Information
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Start Date"
              name="startDate"
              value={fields.startDate}
              onChange={handleChange}
              fullWidth
              disabled={!edit}
              required
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth disabled={!edit} required>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  name="employmentType"
                  value={fields.employmentType}
                  onChange={handleChange}
                  label="Employment Type"
                >
                  <MenuItem value="Full-Time">Full-Time</MenuItem>
                  <MenuItem value="Part-Time">Part-Time</MenuItem>
                  <MenuItem value="Seasonal">Seasonal</MenuItem>
                  <MenuItem value="Temporary">Temporary</MenuItem>
                  <MenuItem value="Contract">Contract</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth disabled={!edit} required>
                <InputLabel>Current Status</InputLabel>
                <Select
                  name="currentStatus"
                  value={fields.currentStatus}
                  onChange={handleChange}
                  label="Current Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="LOA">Leave of Absence</MenuItem>
                  <MenuItem value="Terminated">Terminated</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  name="eligibleForOT"
                  checked={fields.eligibleForOT}
                  onChange={handleChange}
                  disabled={!edit}
                />
              }
              label="Eligible for Overtime"
            />

            {/* Employee Login Password */}
            <TextField
              label="Employee Login Password"
              name="password"
              value={fields.password}
              onChange={handleChange}
              fullWidth
              disabled={!edit}
              type="text"
              helperText="Password for employee to access their dashboard (default: Welcome1)"
              placeholder="Welcome1"
            />

            {/* Punch ID for Time Clock */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                label="Punch ID (Time Clock)"
                name="punchId"
                value={fields.punchId}
                onChange={(e) => {
                  // Only allow 4 digits
                  const value = e.target.value.replace(/\D/g, '').substring(0, 4);
                  setFields(f => ({ ...f, punchId: value }));
                }}
                fullWidth
                disabled={!edit}
                placeholder="1234"
                helperText="4-digit number for employee to clock in/out (must be unique)"
                inputProps={{ 
                  maxLength: 4,
                  style: { 
                    fontSize: '1.2rem', 
                    letterSpacing: '2px', 
                    textAlign: 'center' 
                  }
                }}
              />
              {edit && (
                <Button
                  variant="outlined"
                  onClick={handleGeneratePunchId}
                  sx={{ mb: 3, minWidth: 100 }}
                >
                  Generate
                </Button>
              )}
            </Box>
          </Stack>
        </Box>

        {/* Termination Information (only show if status is Terminated or if there's existing data) */}
        {(isTerminated || fields.endDate || edit) && (
          <>
            <Divider />
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Termination Information
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="End Date"
                  name="endDate"
                  value={fields.endDate}
                  onChange={handleChange}
                  fullWidth
                  disabled={!edit}
                  required={isTerminated}
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText={isTerminated ? "Required for terminated employees" : ""}
                />
                
                <TextField
                  label="Termination Reason"
                  name="terminationReason"
                  value={fields.terminationReason}
                  onChange={handleChange}
                  fullWidth
                  disabled={!edit}
                  multiline
                  rows={3}
                  placeholder="Optional: Reason for termination"
                />
              </Stack>
            </Box>
          </>
        )}

      </Stack>
      )}

      {/* Unsaved changes bar */}
      {edit && hasChanges && (
        <Paper
          elevation={2}
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 3,
            p: 1.25,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Unsaved changes
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
      </Box>
    </Paper>
  );
}
